import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, Card, Button, CircularProgress } from '@mui/material';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import { useUserContext } from '../../hooks/useUserContext';
import { getApiErrorInfo } from '../../services/api';
import billingService, { type Plan } from '../../services/billingService';
import { track } from '../../services/analytics';
import { config } from '../../config';
import { userService } from '../../services/userService';
import PlanSelector from '../../components/billing/PlanSelector';

export interface IndividualPlanSelectionPanelProps {
  onContinue: (planId: string, addonIds: string[], billingPeriod: 'monthly' | 'annual') => Promise<void>;
  /** Pre-select these add-on IDs (e.g. when managing an existing subscription) */
  initialSelectedAddonIds?: string[];
  /** Override the continue button label */
  continueLabel?: string;
}

/**
 * Self-contained individual plan selector.
 * Loads the base plan (auto-included) and add-ons (user-selected).
 * Calls onContinue with the base planId + selected addonIds.
 */
export function IndividualPlanSelectionPanel({ onContinue, initialSelectedAddonIds, continueLabel: continueLabelProp }: IndividualPlanSelectionPanelProps) {
  const [basePlans, setBasePlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>(initialSelectedAddonIds ?? []);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      const [plans, availableAddons] = await Promise.all([
        billingService.getUserPlans(),
        billingService.getAvailableAddons(),
      ]);
      setBasePlans(plans);
      setAddons(availableAddons);
      // Reset checkout loading state when plans load (fixes stuck button when returning from Chargebee)
      setCheckoutLoading(false);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      let msg: string;
      if (apiError.status === 401) {
        msg = 'Authentication required. Please sign in again.';
      } else if (apiError.isNetworkError) {
        msg = 'Unable to connect to the service. Please try again.';
      } else {
        msg = apiError.message || 'Failed to load plans. Please try again.';
      }
      setPlansError(msg);
      setBasePlans([]);
      setAddons([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleToggleAddon = (addonId: string) => {
    setSelectedAddonIds(prev =>
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  // Always annual — filter base plan and addons for yearly period
  const basePlan = basePlans.find(p => p.period === 'year');

  const initialSet = new Set(initialSelectedAddonIds ?? []);
  const hasChanges =
    selectedAddonIds.length !== initialSet.size ||
    selectedAddonIds.some(id => !initialSet.has(id));

  const handleContinue = async () => {
    if (!basePlan) return;
    try {
      setCheckoutLoading(true);
      setCheckoutError(null);
      await onContinue(basePlan.id, selectedAddonIds, 'annual');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setCheckoutError(apiError.message || 'Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <>
      {checkoutError && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setCheckoutError(null)}>
          {checkoutError}
        </Alert>
      )}


      <PlanSelector
        plans={addons}
        plansLoading={plansLoading}
        plansError={plansError}
        onRetry={loadPlans}
        selectedIds={selectedAddonIds}
        billingPeriod="annual"
        onToggle={handleToggleAddon}
        formatPrice={(plan) => `$${plan.price.toFixed(2)}/yr`}
        totalFooter={
          <Typography variant="caption" color="text.secondary">
            per year · billed annually
          </Typography>
        }
        continueLabel={
          continueLabelProp ?? (
            selectedAddonIds.length > 0
              ? `Continue with ${selectedAddonIds.length} module${selectedAddonIds.length !== 1 ? 's' : ''}`
              : 'Continue with base plan'
          )
        }
        onContinue={handleContinue}
        continueLoading={checkoutLoading}
        showContinue={hasChanges}
      />
    </>
  );
}

export default function IndividualPlansPage() {
  const { clerkUser, isLoaded, isSignedIn } = useUserContext();
  const navigate = useNavigate();

  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    track('plan_viewed', { plan_type: 'individual' });
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || !clerkUser || hasInitialized.current) return;
    hasInitialized.current = true;

    const defaultTenantId = config.defaultTenantId;

    // Detect tenant-invited users via Clerk publicMetadata.
    // When a tenant admin invites someone, InviteUserAsync sets tenantId in
    // the Clerk user's publicMetadata. These users should never see the
    // individual plans page — their subscription is managed by their org.
    const metaTenantId = (clerkUser.publicMetadata as Record<string, unknown>)?.tenantId as string | undefined;
    if (metaTenantId && metaTenantId !== defaultTenantId) {
      // Still call createBackendAccount to activate the pending invited user,
      // but skip the plans UI regardless — redirect directly to background setup.
      userService.createBackendAccount({
        authProviderId: clerkUser.id,
        email: clerkUser.primaryEmailAddress?.emailAddress || '',
        firstName: clerkUser.firstName || '',
        lastName: clerkUser.lastName || '',
      })
        .catch(() => {/* activation errors handled by GetMe fallback */})
        .finally(() => navigate('/auth/individual-background', { replace: true }));
      return;
    }

    userService.createBackendAccount({
      authProviderId: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      firstName: clerkUser.firstName || '',
      lastName: clerkUser.lastName || '',
    })
      .then(async (profile) => {
        const tenantId = profile?.tenantId;
        const role = profile?.role;

        if (role === 'user' && tenantId && tenantId !== defaultTenantId) {
          navigate('/auth/individual-background', { replace: true });
          return;
        }

        if (role === 'tenant-admin' && tenantId) {
          const subscription = await billingService.getSubscription(tenantId);
          if (subscription) {
            navigate('/auth/individual-background', { replace: true });
            return;
          }
        }

        setIsInitializing(false);
      })
      .catch(() => {
        setInitError('Unable to set up your account. Please try again.');
        setIsInitializing(false);
      });
  }, [isLoaded, isSignedIn, clerkUser]);

  const handleContinue = async (planId: string, addonIds: string[], _billingPeriod: 'monthly' | 'annual') => {
    track('checkout_started', { plan_type: 'individual', item_price_id: planId });
    sessionStorage.setItem('checkout_flow', 'signup');
    const redirectUrl = `${window.location.origin}/subscription-confirmation`;
    const hostedPage = await billingService.createUserSubscriptionCheckout({
      planId,
      addonIds,
      redirectUrl,
    });
    window.location.href = hostedPage.url;
  };

  if (!isLoaded || isInitializing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: 'surface.subtle' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h6" color="error" gutterBottom>Failed to set up account</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{initError}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'surface.subtle' }}>
      {/* Hero header */}
      <Box
        sx={{
          background: 'linear-gradient(160deg, #0c200f 0%, #1a4328 55%, #0d3d1c 100%)',
          pt: { xs: 6, md: 8 },
          pb: { xs: 5, md: 6 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle grid overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.06,
            backgroundImage: `
              linear-gradient(rgba(86,151,55,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(86,151,55,0.5) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.45)',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 3,
              textTransform: 'uppercase',
              mb: 2.5,
            }}
          >
            CPAx Academy
          </Typography>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              mb: 1.5,
              fontSize: { xs: 26, md: 34 },
              letterSpacing: '-0.5px',
            }}
          >
            My Subscription
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.5)' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
              Annual billing
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content card overlapping header */}
      <Box sx={{ maxWidth: 620, mx: 'auto', px: { xs: 2, sm: 3 }, mt: 4, pb: 8 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            p: { xs: 3, sm: 4 },
            bgcolor: 'white',
          }}
        >
          <IndividualPlanSelectionPanel onContinue={handleContinue} />
        </Card>
      </Box>
    </Box>
  );
}
